import type { QuotationDto } from '../dtos/quotation.dto';
import type { QuotationParams } from '../interfaces/quotation-params.interface';

import { toRoundedAmount } from '../../../functions/to-rounded-amount.function';

/**
 * Calculates quotation totals from discountable and non-discountable base totals.
 *
 * Intermediate values are kept at full precision and only the emitted figures
 * are rounded (to 2 decimals). For each net/tax/gross group, gross is anchored
 * and the tax portion is derived as `gross - net`, so `net + tax === gross`
 * holds exactly without pre-rounding the components and accumulating drift.
 *
 * Discount fields are only included when `isStandardDiscountEnabled` is true
 * (even if the resulting magnitude is 0). When the feature is off they are
 * omitted — null is never used.
 */
export function calculateQuotation(params: QuotationParams): QuotationDto {
  const discountableNet = Number(params.discountableBaseTotalNet ?? 0);
  const discountableGross = Number(params.discountableBaseTotalGross ?? 0);

  const standardNonDiscountedNet = Number(
    params.standardNonDiscountedBaseTotalNet ?? 0,
  );
  const standardNonDiscountedGross = Number(
    params.standardNonDiscountedBaseTotalGross ?? 0,
  );

  const nonDiscountableNet = Number(params.nonDiscountableBaseTotalNet ?? 0);
  const nonDiscountableGross = Number(
    params.nonDiscountableBaseTotalGross ?? 0,
  );

  const isStandardDiscountEnabled = params.isStandardDiscountEnabled === true;
  const standardDiscountPercentage = isStandardDiscountEnabled
    ? Number(params.standardDiscountPercentage ?? 0)
    : 0;
  const discountRate =
    standardDiscountPercentage > 0 ? standardDiscountPercentage / 100 : 0;

  // Full-precision intermediates; rounding happens only on emitted figures.
  const discountNetRaw = discountableNet * discountRate;
  const discountGrossRaw = discountableGross * discountRate;

  // Anchor each group on gross and derive tax as the residual (gross - net) so
  // the displayed net + tax always reconciles to gross to the cent.
  const computedDiscountGross = toRoundedAmount(discountGrossRaw);
  const computedDiscountNet = toRoundedAmount(discountNetRaw);
  const computedDiscountTax = toRoundedAmount(
    computedDiscountGross - computedDiscountNet,
  );

  const standardGrossRaw = discountableGross + standardNonDiscountedGross;
  const standardNetRaw = discountableNet + standardNonDiscountedNet;

  const computedStandardGross = toRoundedAmount(standardGrossRaw);
  const computedStandardNet = toRoundedAmount(standardNetRaw);
  const computedStandardTax = toRoundedAmount(
    computedStandardGross - computedStandardNet,
  );

  const computedExtrasGross = toRoundedAmount(nonDiscountableGross);
  const computedExtrasNet = toRoundedAmount(nonDiscountableNet);
  const computedExtrasTax = toRoundedAmount(
    computedExtrasGross - computedExtrasNet,
  );

  const computedTotalGross = toRoundedAmount(
    standardGrossRaw - discountGrossRaw + nonDiscountableGross,
  );
  const computedTotalNet = toRoundedAmount(
    standardNetRaw - discountNetRaw + nonDiscountableNet,
  );
  const computedTotalTax = toRoundedAmount(
    computedTotalGross - computedTotalNet,
  );

  const computedSavingsAmount = params.computedSavingsAmount;

  const result: QuotationDto = {
    computedStandardNet,
    computedStandardTax,
    computedStandardGross,
    computedExtrasNet,
    computedExtrasTax,
    computedExtrasGross,
    computedTotalNet,
    computedTotalTax,
    computedTotalGross,
    computedSavingsAmount,
    computedSavingsPercentage: params.computedSavingsPercentage,
    computedOriginalStandardAmount: toRoundedAmount(
      computedStandardGross + Number(computedSavingsAmount ?? 0),
    ),
    duration: params.duration,
    durationUnit: params.durationUnit,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
  };

  if (isStandardDiscountEnabled) {
    result.computedDiscountNet = computedDiscountNet;
    result.computedDiscountTax = computedDiscountTax;
    result.computedDiscountGross = computedDiscountGross;
  }

  return result;
}
